import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import defaultClient from '@src/api/clients/default';
import paths from '@src/paths';

const STATUS_OPTIONS = [
  { value: 'new_business', label: 'New Business' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'active_new', label: 'Active - New' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'off', label: 'Off' },
];

const completionKeys = [
  { key: 'contract', label: 'Contract' },
  { key: 'basics', label: 'Client contacts' },
  { key: 'assets', label: 'Creative assets' },
  { key: 'offers', label: 'Offers' },
  { key: 'crm', label: 'Lead routing & notifications' },
  { key: 'messaging', label: 'Sales schedule' },
  { key: 'launch', label: 'Target launch' },
  { key: 'billing', label: 'Billing' },
  { key: 'approvals', label: 'Approvals' },
];

function StatusBadge({ done }) {
  const Icon = done ? CheckCircleIcon : HourglassEmptyIcon;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
      <Icon fontSize="small" color={done ? 'success' : 'disabled'} />
      <Typography variant="caption" color={done ? 'success.main' : 'text.secondary'}>
        {done ? 'Done' : 'Pending'}
      </Typography>
    </Stack>
  );
}

function OnboardingListPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(['new_business', 'onboarding']);
  const statusLabelMap = useMemo(
    () => Object.fromEntries(STATUS_OPTIONS.map((opt) => [opt.value, opt.label])),
    []
  );
  const statusColorMap = useMemo(
    () => ({
      new_business: 'info',
      onboarding: 'primary',
      active_new: 'success',
      active: 'success',
      paused: 'warning',
      off: 'default',
    }),
    []
  );
  const statusPriority = useMemo(
    () => ({
      new_business: 0,
      onboarding: 1,
    }),
    []
  );

  const statusParam = useMemo(() => selectedStatuses.join(','), [selectedStatuses]);
  const filteredClients = useMemo(() => {
    const sortByStatusThenName = (a, b) => {
      const aPriority = statusPriority[a.status] ?? 99;
      const bPriority = statusPriority[b.status] ?? 99;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return (a.name || '').localeCompare(b.name || '');
    };

    if (!selectedStatuses.length) return [...clients].sort(sortByStatusThenName);
    const allowed = new Set(selectedStatuses);
    return [...clients.filter((c) => allowed.has(c.status))].sort(sortByStatusThenName);
  }, [clients, selectedStatuses, statusPriority]);

  useEffect(() => {
    setLoading(true);
    defaultClient
      .request({
        url: 'clients/onboarding/',
        method: 'GET',
        params: { status: statusParam },
      })
      .then((resp) => {
        setClients(resp?.data?.results || []);
      })
      .catch(() => {
        setClients([]);
      })
      .finally(() => setLoading(false));
  }, [statusParam]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4">Client Onboarding</Typography>
          <Typography color="text.secondary">
            Track onboarding progress across clients. Defaults to new business + onboarding.
          </Typography>
        </Box>
        <TextField
          select
          fullWidth
          size="small"
          label="Filter by status"
          value={selectedStatuses}
          onChange={(e) => setSelectedStatuses(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
          SelectProps={{ multiple: true, renderValue: (vals) => vals.map((v) => STATUS_OPTIONS.find((o) => o.value === v)?.label || v).join(', ') }}
          sx={{ minWidth: { md: 280, xs: '100%' } }}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            {loading && <LinearProgress />}
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  minWidth: 960,
                  '& td, & th': { py: 0.75, px: 1 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Projections</TableCell>
                    <TableCell>Progress</TableCell>
                    {completionKeys.map((c) => (
                      <TableCell key={c.key} align="center" sx={{ width: 90 }}>
                        {c.label}
                      </TableCell>
                    ))}
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client.id} hover>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="subtitle2">{client.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.city || ''}{client.city && client.state ? ', ' : ''}{client.state || ''}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabelMap[client.status] || client.status}
                          size="small"
                          color={statusColorMap[client.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="text"
                          size="small"
                          href={paths.projections.forClient(client.slug)}
                        >
                          Projections
                        </Button>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <Stack spacing={0.5}>
                          <LinearProgress
                            variant="determinate"
                            value={client.progress || 0}
                            sx={{ height: 6, borderRadius: 999 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {client.progress || 0}% complete
                          </Typography>
                        </Stack>
                      </TableCell>
                      {completionKeys.map((c) => (
                        <TableCell key={c.key} align="center">
                          <StatusBadge done={client.completion?.[c.key]} />
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Stack direction="column" spacing={0.5}>
                          <Button
                            variant="outlined"
                            size="small"
                            href={paths.onboarding.forClient(client.slug)}
                          >
                            Open onboarding
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            href={paths.onboarding.internal(client.slug)}
                          >
                            Internal workflow
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!filteredClients.length && !loading && (
                    <TableRow>
                      <TableCell colSpan={completionKeys.length + 4}>
                        <Typography color="text.secondary" align="center">
                          No clients match this filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default OnboardingListPage;
